import React, { createContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

export const ThemeContext = createContext();

// Get initial theme state from localStorage
const getInitialThemeState = () => {
	const darkModePreference = localStorage.getItem('dark-mode');
	const isDarkMode = darkModePreference === 'true';
	const isFullscreen = localStorage.getItem('fullscreen-mode') === 'true';
	const audioEnabled = localStorage.getItem('audio-enabled') !== 'false'; // default on
	// 'compact' was a legacy tileSize value; compact density is now column-driven.
	// Migrate any stale 'compact' entry to 'normal' on startup.
	const rawTileSize = localStorage.getItem('tile-size') || 'normal';
	const tileSize = rawTileSize === 'compact' ? 'normal' : rawTileSize;
	if (rawTileSize === 'compact') localStorage.setItem('tile-size', 'normal');
	const showAlertTray = localStorage.getItem('show-alert-tray') !== 'false'; // default on
	const viewMode = localStorage.getItem('view-mode') || 'tiles'; // 'tiles' | 'list'
	const tileSortOrder = localStorage.getItem('tile-sort-order') || 'manual';
	const tileColumns = parseInt(localStorage.getItem('tile-columns') || '5', 10);
	const tileMinHeight = parseInt(localStorage.getItem('tile-min-height') || '0', 10);
	// This user's personal manual agent ordering (array of agent id strings). Server is the
	// source of truth; localStorage is just an offline fallback.
	let manualAgentOrder = [];
	try { manualAgentOrder = JSON.parse(localStorage.getItem('manual-agent-order') || '[]'); }
	catch { manualAgentOrder = []; }
	if (!Array.isArray(manualAgentOrder)) manualAgentOrder = [];

	return {
		darkMode: isDarkMode,
		sidebarCollapsed: false,
		rightToolbarCollapsed: false,
		createAgentSignal: 0,
		createFromTemplateSignal: 0,
		fullscreen: isFullscreen,
		audioEnabled,
		tileSize,
		showAlertTray,
		viewMode,
		tileSortOrder,
		tileColumns,
		tileMinHeight,
		manualAgentOrder,
		userGuideOpen: false,
	};
};

function themeReducer(state, action) {
	switch (action.type) {
	case 'toggle-sidebar':
		return {
			...state,
			sidebarCollapsed: action.collapsed !== undefined ? action.collapsed : !state.sidebarCollapsed
		};
	case 'toggle-right-toolbar':
		return {
			...state,
			rightToolbarCollapsed: action.collapsed !== undefined ? action.collapsed : !state.rightToolbarCollapsed
		};
	case 'trigger-create-agent':
		return { ...state, createAgentSignal: state.createAgentSignal + 1 };
	case 'trigger-create-from-template':
		return { ...state, createFromTemplateSignal: state.createFromTemplateSignal + 1 };
	case 'toggle-fullscreen': {
		const newFullscreen = action.fullscreen !== undefined ? action.fullscreen : !state.fullscreen;
		localStorage.setItem('fullscreen-mode', newFullscreen.toString());
		return {
			...state,
			fullscreen: newFullscreen
		};
	}
	case 'toggle-audio': {
		const newAudio = action.audioEnabled !== undefined ? action.audioEnabled : !state.audioEnabled;
		localStorage.setItem('audio-enabled', newAudio.toString());
		return {
			...state,
			audioEnabled: newAudio
		};
	}
	case 'toggle-dark-mode': {
		const newDarkMode = action.darkMode !== undefined ? action.darkMode : !state.darkMode;
		// Persist to localStorage
		localStorage.setItem('dark-mode', newDarkMode.toString());
		// Update HTML data-bs-theme attribute for Bootstrap
		document.documentElement.setAttribute('data-bs-theme', newDarkMode ? 'dark' : 'light');
		return {
			...state,
			darkMode: newDarkMode
		};
	}
	case 'load-preferences': {
		// Apply preferences loaded from the server (cross-device sync)
		const { darkMode, audioEnabled, fullscreenMode, tileSize, showAlertTray, viewMode, tileSortOrder, tileColumns, tileMinHeight, manualAgentOrder } = action.preferences;
		const validHeights = [0, 120, 200, 280];
		const safeTileColumns = tileColumns != null ? Math.max(1, Math.min(7, tileColumns)) : null;
		const safeTileMinHeight = tileMinHeight != null
			? (validHeights.includes(tileMinHeight) ? tileMinHeight : 0)
			: null;
		localStorage.setItem('dark-mode', darkMode.toString());
		localStorage.setItem('audio-enabled', audioEnabled.toString());
		localStorage.setItem('fullscreen-mode', fullscreenMode.toString());
		if (tileSize) localStorage.setItem('tile-size', tileSize);
		localStorage.setItem('show-alert-tray', (showAlertTray !== false).toString());
		if (viewMode) localStorage.setItem('view-mode', viewMode);
		if (tileSortOrder) localStorage.setItem('tile-sort-order', tileSortOrder);
		if (safeTileColumns != null) localStorage.setItem('tile-columns', safeTileColumns.toString());
		if (safeTileMinHeight != null) localStorage.setItem('tile-min-height', safeTileMinHeight.toString());
		const safeManualOrder = Array.isArray(manualAgentOrder) ? manualAgentOrder : state.manualAgentOrder;
		localStorage.setItem('manual-agent-order', JSON.stringify(safeManualOrder));
		document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
		return {
			...state,
			darkMode,
			audioEnabled,
			fullscreen: fullscreenMode,
			tileSize: tileSize || state.tileSize,
			showAlertTray: showAlertTray !== false,
			viewMode: viewMode || state.viewMode,
			tileSortOrder: tileSortOrder || state.tileSortOrder,
			tileColumns: safeTileColumns != null ? safeTileColumns : state.tileColumns,
			tileMinHeight: safeTileMinHeight != null ? safeTileMinHeight : state.tileMinHeight,
			manualAgentOrder: safeManualOrder
		};
	}
	case 'set-manual-agent-order': {
		const newOrder = Array.isArray(action.manualAgentOrder) ? action.manualAgentOrder : [];
		localStorage.setItem('manual-agent-order', JSON.stringify(newOrder));
		return {
			...state,
			manualAgentOrder: newOrder
		};
	}
	case 'set-tile-size': {
		const newTileSize = action.tileSize;
		localStorage.setItem('tile-size', newTileSize);
		return {
			...state,
			tileSize: newTileSize
		};
	}
	case 'set-show-alert-tray': {
		const newShow = action.showAlertTray;
		localStorage.setItem('show-alert-tray', newShow.toString());
		return {
			...state,
			showAlertTray: newShow
		};
	}
	case 'set-view-mode': {
		const newViewMode = action.viewMode;
		localStorage.setItem('view-mode', newViewMode);
		return {
			...state,
			viewMode: newViewMode
		};
	}
	case 'set-tile-sort-order': {
		const newSortOrder = action.tileSortOrder;
		localStorage.setItem('tile-sort-order', newSortOrder);
		return {
			...state,
			tileSortOrder: newSortOrder
		};
	}
	case 'set-tile-columns': {
		const newCols = Math.max(1, Math.min(7, action.tileColumns));
		localStorage.setItem('tile-columns', newCols.toString());
		return {
			...state,
			tileColumns: newCols
		};
	}
	case 'set-tile-min-height': {
		const validHeights = [0, 120, 200, 280];
		const newHeight = validHeights.includes(action.tileMinHeight) ? action.tileMinHeight : 0;
		localStorage.setItem('tile-min-height', newHeight.toString());
		return {
			...state,
			tileMinHeight: newHeight
		};
	}
	case 'toggle-user-guide': {
		const newOpen = action.open !== undefined ? action.open : !state.userGuideOpen;
		return { ...state, userGuideOpen: newOpen };
	}
	default:
		throw new Error(`Unrecognized action in ThemeContext: ${action.type}`);
	}
}

export const ThemeProvider = ({ children }) => {
	const [state, dispatch] = useReducer(themeReducer, null, getInitialThemeState);

	const toggleAudio = useCallback((audioEnabled) => {
		dispatch({
			type: 'toggle-audio',
			audioEnabled
		});
	}, []);

	const toggleFullscreen = useCallback((fullscreen) => {
		dispatch({
			type: 'toggle-fullscreen',
			fullscreen
		});
	}, []);

	const toggleSidebar = useCallback((collapsed) => {
		dispatch({ type: 'toggle-sidebar', collapsed });
	}, []);

	const toggleRightToolbar = useCallback((collapsed) => {
		dispatch({ type: 'toggle-right-toolbar', collapsed });
	}, []);

	const triggerCreateAgent = useCallback(() => {
		dispatch({ type: 'trigger-create-agent' });
	}, []);

	const triggerCreateFromTemplate = useCallback(() => {
		dispatch({ type: 'trigger-create-from-template' });
	}, []);

	const toggleDarkMode = useCallback((darkMode) => {
		dispatch({
			type: 'toggle-dark-mode',
			darkMode
		});
	}, []);

	const setTileSize = useCallback((tileSize) => {
		dispatch({
			type: 'set-tile-size',
			tileSize
		});
	}, []);

	const setShowAlertTray = useCallback((showAlertTray) => {
		dispatch({
			type: 'set-show-alert-tray',
			showAlertTray
		});
	}, []);

	const setViewMode = useCallback((viewMode) => {
		dispatch({
			type: 'set-view-mode',
			viewMode
		});
	}, []);

	const setTileSortOrder = useCallback((tileSortOrder) => {
		dispatch({
			type: 'set-tile-sort-order',
			tileSortOrder
		});
	}, []);

	const setTileColumns = useCallback((tileColumns) => {
		dispatch({
			type: 'set-tile-columns',
			tileColumns
		});
	}, []);

	const setTileMinHeight = useCallback((tileMinHeight) => {
		dispatch({
			type: 'set-tile-min-height',
			tileMinHeight
		});
	}, []);

	const setManualAgentOrder = useCallback((manualAgentOrder) => {
		dispatch({
			type: 'set-manual-agent-order',
			manualAgentOrder
		});
	}, []);

	const loadPreferences = useCallback((preferences) => {
		dispatch({
			type: 'load-preferences',
			preferences
		});
	}, []);

	const toggleUserGuide = useCallback((open) => {
		dispatch({ type: 'toggle-user-guide', open });
	}, []);

	// Apply dark mode on initial load
	useEffect(() => {
		document.documentElement.setAttribute('data-bs-theme', state.darkMode ? 'dark' : 'light');
	}, []);

	const actions = useMemo(() => ({
		toggleSidebar,
		toggleRightToolbar,
		triggerCreateAgent,
		triggerCreateFromTemplate,
		toggleDarkMode,
		toggleFullscreen,
		toggleAudio,
		loadPreferences,
		setTileSize,
		setShowAlertTray,
		setViewMode,
		setTileSortOrder,
		setTileColumns,
		setTileMinHeight,
		setManualAgentOrder,
		toggleUserGuide
	}), [
		toggleSidebar,
		toggleRightToolbar,
		triggerCreateAgent,
		triggerCreateFromTemplate,
		toggleDarkMode,
		toggleFullscreen,
		toggleAudio,
		loadPreferences,
		setTileSize,
		setShowAlertTray,
		setViewMode,
		setTileSortOrder,
		setTileColumns,
		setTileMinHeight,
		setManualAgentOrder,
		toggleUserGuide
	]);

	const contextValue = useMemo(() => ({ state, actions }), [state, actions]);

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
};

ThemeProvider.propTypes = {
	children: PropTypes.node.isRequired
};
